<script setup>
import { ref, computed } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import moment from 'moment'
import rmLogo from '@/assets/logo.svg'
import { getRestaurantImageUrl } from '@core/utils/restaurantImage'

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  isOwn: {
    type: Boolean,
    default: false,
  },

  // First in a consecutive group: show avatar + name
  showSender: {
    type: Boolean,
    default: true,
  },

  // Restaurant from the parent conversation — used to resolve name + avatar
  // for restaurant-sent messages (the per-message senderProfile snapshot
  // doesn't carry the restaurant name or Google Places fallback image).
  restaurant: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['delete'])

const hovered = ref(false)
const showDeleteConfirm = ref(false)

const isDeleted = computed(() => !!props.message.deletedAt)

const isSupport = computed(() => props.message.senderType === 'support')

const senderName = computed(() => {
  if (props.message.senderType === 'support') {
    return 'RM Support'
  }
  if (props.message.senderType === 'restaurant') {
    return props.message.senderProfile?.name
      || props.restaurant?.name
      || (props.message.senderProfile?.firstName
        ? `${props.message.senderProfile.firstName} ${props.message.senderProfile.lastName || ''}`.trim()
        : 'Restaurant')
  }
  const p = props.message.senderProfile
  if (p) return `${p.firstName || ''} ${p.lastName || ''}`.trim()

  return 'Unknown'
})

const restaurantAvatarUrl = computed(() => {
  if (props.message.senderType !== 'restaurant') return null
  const snapshotImageId = props.message.senderProfile?.profileImageId
  if (snapshotImageId) return `/media/thumb/${snapshotImageId}`

  return getRestaurantImageUrl(props.restaurant, { includeEventSpaces: false })
})

const timestamp = computed(() => {
  if (!props.message.createdAt) return ''

  return moment(props.message.createdAt).format('h:mm A')
})

// Split message text into plain + URL segments so URLs render as anchor tags.
// Using text interpolation (not v-html) keeps this XSS-safe; the regex only
// matches http(s):// and www. URLs so href values can't be `javascript:` etc.
const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi
const TRAILING_PUNCT = /[.,;:!?)\]}'"]+$/

function linkifySegments(text) {
  if (!text) return []
  const segments = []
  let lastIndex = 0
  let match
  URL_REGEX.lastIndex = 0
  while ((match = URL_REGEX.exec(text)) !== null) {
    const matchStart = match.index
    const raw = match[0]
    const trimmed = raw.replace(TRAILING_PUNCT, '')
    const trailing = raw.slice(trimmed.length)

    if (matchStart > lastIndex) {
      segments.push({ type: 'text', text: text.slice(lastIndex, matchStart) })
    }
    const href = trimmed.startsWith('www.') ? `https://${trimmed}` : trimmed
    segments.push({ type: 'url', text: trimmed, href })
    if (trailing) {
      segments.push({ type: 'text', text: trailing })
    }
    lastIndex = matchStart + raw.length
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', text: text.slice(lastIndex) })
  }

  return segments
}

const textSegments = computed(() => linkifySegments(props.message.text))

// ─── Attachments + link preview ──────────────────────────────────────────────
const attachments = computed(() =>
  Array.isArray(props.message.attachments) ? props.message.attachments : [],
)
const imageAttachments = computed(() => attachments.value.filter(a => a.kind === 'image'))
const fileAttachments = computed(() => attachments.value.filter(a => a.kind !== 'image'))
const linkPreview = computed(() => props.message.linkPreview || null)

function formatBytes(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`

  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function confirmDelete() {
  showDeleteConfirm.value = false
  emit('delete', props.message._id)
}
</script>

<template>
  <div
    class="msg-row"
    :class="{ 'msg-row--own': isOwn, 'msg-row--grouped': !showSender }"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <!-- Avatar column (always on left) -->
    <div class="msg-avatar-col">
      <template v-if="showSender">
        <!-- RM logo for support messages -->
        <VAvatar
          v-if="isSupport"
          size="36"
          color="white"
        >
          <VImg :src="rmLogo" />
        </VAvatar>
        <VAvatar
          v-else-if="message.senderType === 'restaurant'"
          size="36"
          color="primary-light"
        >
          <VImg
            v-if="restaurantAvatarUrl"
            :src="restaurantAvatarUrl"
            cover
          />
          <VIcon
            v-else
            size="18"
            color="primary"
          >
            mdi-silverware-fork-knife
          </VIcon>
        </VAvatar>
        <VAvatar
          v-else-if="message.senderProfile?.profileImageId"
          size="36"
        >
          <VImg :src="mediaUrl(message.senderProfile.profileImageId, 'thumb')" />
        </VAvatar>
        <VAvatar
          v-else
          size="36"
          :color="isOwn ? 'primary-light' : 'grey-lighten-3'"
        >
          <span
            v-if="message.senderProfile"
            class="text-caption font-weight-medium"
            :style="isOwn ? 'color: var(--ds-brand-primary-dark, #3F998F)' : 'color: #666'"
          >
            {{ message.senderProfile.firstName?.[0] || '' }}{{ message.senderProfile.lastName?.[0] || '' }}
          </span>
          <VIcon
            v-else
            size="18"
            color="grey"
          >
            mdi-account
          </VIcon>
        </VAvatar>
      </template>
      <!-- Spacer to keep text aligned when avatar is hidden -->
      <div
        v-else
        style="width: 36px; flex-shrink: 0;"
      />
    </div>

    <!-- Content -->
    <div class="msg-content flex-1 min-w-0">
      <!-- Sender name + timestamp (first in group only) -->
      <div
        v-if="showSender"
        class="d-flex align-baseline gap-2 mb-1"
      >
        <span
          class="msg-sender-name"
          :class="isSupport ? '' : isOwn ? 'text-primary-dark' : ''"
        >
          {{ isSupport ? 'RM Support' : isOwn ? 'You' : senderName }}
        </span>
        <span class="msg-timestamp">{{ timestamp }}</span>
      </div>

      <!-- Message text row -->
      <div class="msg-text-row">
        <!-- Deleted -->
        <span
          v-if="isDeleted"
          class="text-caption font-italic text-medium-emphasis"
        >This message was deleted</span>

        <!-- Text -->
        <span
          v-else-if="message.text"
          class="msg-text"
          :class="{ 'msg-text--own': isOwn }"
        ><template
          v-for="(seg, i) in textSegments"
          :key="i"
        ><a
          v-if="seg.type === 'url'"
          :href="seg.href"
          target="_blank"
          rel="noopener noreferrer"
          class="msg-text-link"
        >{{ seg.text }}</a><template v-else>{{ seg.text }}</template></template></span>

        <!-- Inline timestamp for grouped messages -->
        <span
          v-if="!showSender && hovered"
          class="msg-inline-time text-caption text-disabled ml-2"
        >{{ timestamp }}</span>

        <!-- Image attachments -->
        <div
          v-if="message.imageIds && message.imageIds.length && !isDeleted"
          class="msg-images mt-1 d-flex flex-wrap gap-2"
        >
          <a
            v-for="imgId in message.imageIds"
            :key="imgId"
            :href="mediaUrl(imgId, 'regular')"
            target="_blank"
            rel="noopener noreferrer"
            class="msg-image-link"
          >
            <img
              :src="mediaUrl(imgId, 'thumb')"
              class="msg-image-thumb rounded"
            >
          </a>
        </div>

        <!-- Image attachments (S3/CDN-backed) -->
        <div
          v-if="imageAttachments.length && !isDeleted"
          class="msg-images mt-1 d-flex flex-wrap gap-2"
        >
          <a
            v-for="att in imageAttachments"
            :key="att.url"
            :href="att.url"
            target="_blank"
            rel="noopener noreferrer"
            class="msg-image-link"
          >
            <img
              :src="att.url"
              :alt="att.name || 'Image attachment'"
              class="msg-image-thumb rounded"
            >
          </a>
        </div>

        <!-- PDF / file attachments -->
        <div
          v-if="fileAttachments.length && !isDeleted"
          class="msg-files mt-1"
        >
          <a
            v-for="att in fileAttachments"
            :key="att.url"
            :href="att.url"
            target="_blank"
            rel="noopener noreferrer"
            download
            class="msg-file"
          >
            <VIcon
              size="22"
              color="error"
            >
              mdi-file-pdf-box
            </VIcon>
            <span class="msg-file-meta">
              <span class="msg-file-name">{{ att.name || 'Document.pdf' }}</span>
              <span
                v-if="att.size"
                class="msg-file-size"
              >{{ formatBytes(att.size) }}</span>
            </span>
            <VIcon
              size="16"
              class="msg-file-dl"
            >
              mdi-download
            </VIcon>
          </a>
        </div>

        <!-- Link preview -->
        <a
          v-if="linkPreview && !isDeleted"
          :href="linkPreview.url"
          target="_blank"
          rel="noopener noreferrer"
          class="msg-link-preview mt-1"
        >
          <img
            v-if="linkPreview.imageUrl"
            :src="linkPreview.imageUrl"
            alt=""
            class="msg-link-preview-img"
          >
          <span class="msg-link-preview-text">
            <span
              v-if="linkPreview.siteName"
              class="msg-link-preview-site"
            >{{ linkPreview.siteName }}</span>
            <span class="msg-link-preview-title">{{ linkPreview.title || linkPreview.url }}</span>
            <span
              v-if="linkPreview.description"
              class="msg-link-preview-desc"
            >{{ linkPreview.description }}</span>
          </span>
        </a>

        <!-- Reservation pill -->
        <div
          v-if="message.reservationId && !isDeleted"
          class="mt-1"
        >
          <VChip
            size="x-small"
            variant="tonal"
            color="primary"
            prepend-icon="mdi-calendar-check"
          >
            Re: Reservation
          </VChip>
        </div>
      </div>

      <!-- Hover action: delete own message -->
      <div
        v-if="isOwn && !isDeleted"
        class="msg-actions"
        :class="{ 'msg-actions--visible': hovered }"
      >
        <VBtn
          icon
          size="x-small"
          variant="text"
          color="medium-emphasis"
          @click="showDeleteConfirm = true"
        >
          <VIcon size="14">
            mdi-trash-can-outline
          </VIcon>
        </VBtn>
      </div>
    </div>
  </div>

  <!-- Delete confirm dialog -->
  <ConfirmDialog
    v-model:is-dialog-visible="showDeleteConfirm"
    confirmation-msg="Delete this message?"
    confirm-btn-text="Delete"
    cancel-btn-text="Cancel"
    @confirm="confirmDelete"
  />
</template>

<style scoped>
.msg-row {
  display: flex;
  gap: 10px;
  padding: 6px 16px;
  align-items: flex-start;
  position: relative;
}

/* Tighter spacing for consecutive messages in a group */
.msg-row--grouped {
  padding-top: 2px;
  padding-bottom: 2px;
}

/* Subtle own-message left accent */
.msg-row--own {
  background: transparent;
}

.msg-row--own:hover {
  background: rgba(63, 153, 143, 0.04);
}

.msg-row:not(.msg-row--own):hover {
  background: var(--ds-surface-alt, #f8f8f9);
}

.msg-avatar-col {
  flex-shrink: 0;
  padding-top: 2px;
}

.msg-content {
  flex: 1;
  min-width: 0;
}

.msg-sender-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
}

.msg-sender-name.text-primary-dark {
  color: var(--ds-brand-primary-dark, #3F998F);
}

.msg-timestamp {
  font-size: 11px;
  color: var(--ds-text-muted);
  white-space: nowrap;
  line-height: 1.4;
}

.msg-text-row {
  position: relative;
}

.msg-text {
  font-size: 14px;
  line-height: 1.5;
  color: #1a1a1a;
  white-space: pre-wrap;
  word-break: break-word;
  display: inline;
}

/* Own messages get a very subtle teal tint on the text */
.msg-text--own {
  color: #1a1a1a;
}

.msg-inline-time {
  font-size: 11px;
  white-space: nowrap;
  vertical-align: middle;
}

.msg-text-link {
  color: var(--ds-brand-primary-dark, #3F998F);
  text-decoration: underline;
  word-break: break-all;
}

.msg-text-link:hover {
  text-decoration: none;
}

.msg-actions {
  position: absolute;
  top: 0;
  right: 0;
  opacity: 0;
  transition: opacity 0.1s;
}

.msg-actions--visible {
  opacity: 1;
}

.profile-toggle {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--ds-brand-primary-dark, #3F998F);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
}

.profile-snapshot {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  max-width: 400px;
}

.msg-image-thumb {
  max-width: 200px;
  max-height: 150px;
  object-fit: cover;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.msg-image-link {
  display: inline-block;
  line-height: 0;
}

/* ─── PDF / file attachments ─────────────────────────────────────────────── */
.msg-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.msg-file {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 260px;
  padding: 8px 10px;
  background: var(--ds-surface-alt, #f8f8f9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.12s ease;
}

.msg-file:hover {
  background: rgba(0, 0, 0, 0.05);
}

.msg-file-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.msg-file-name {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.msg-file-size {
  font-size: 11px;
  color: var(--ds-text-muted, #777);
}

.msg-file-dl {
  color: var(--ds-text-muted, #777);
  flex-shrink: 0;
}

/* ─── Link preview ───────────────────────────────────────────────────────── */
.msg-link-preview {
  display: flex;
  gap: 10px;
  max-width: 400px;
  padding: 8px;
  background: var(--ds-surface-alt, #f8f8f9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.12s ease;
}

.msg-link-preview:hover {
  background: rgba(0, 0, 0, 0.05);
}

.msg-link-preview-img {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 6px;
}

.msg-link-preview-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.msg-link-preview-site {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--ds-text-muted, #777);
}

.msg-link-preview-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.msg-link-preview-desc {
  margin-top: 2px;
  font-size: 12px;
  color: var(--ds-text-muted, #777);
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
